import * as THREE from "three";
import { descendantsOf, isDescendantOf } from "./nodeTree";
import {
  projectionOutputPassCompensation,
  projectionEnabled,
} from "./projection";
import { parseColorToRgba, rgbaToHex } from "@/components/ui/ColorPicker";

// Lights are excluded here and handled by buildSemanticLights/
// computeLightBounds instead — a plain light node itself has no geometry
// (only its optional visible marker sphere does, see sceneGraph.js), so
// mixing it into this mesh-oriented candidate set would need special-casing
// anyway; better to give it its own dedicated path from the start (see
// buildSceneJson's "lights" array).
const isSemantic = (node) =>
  node.type !== "light" &&
  (!!node.aiDescription || (node.aiReferenceImages?.length ?? 0) > 0);

// Groups every "semantic" node (has an AI description or reference image)
// together with its full descendant subtree. A semantic node nested inside
// another semantic node's subtree is absorbed into the ancestor's group
// rather than becoming a second entry — "if the object is a parent to
// children, all children describe as one group" reads as one scene.json
// entry per top-level semantic subtree.
export function buildSemanticGroups(nodes) {
  const byId = new Map(nodes.map((n) => [n._id, n]));
  const depthOf = (id) => {
    let d = 0;
    let cur = byId.get(id);
    while (cur?.parent) {
      d++;
      cur = byId.get(cur.parent);
    }
    return d;
  };

  const candidates = nodes
    .filter(isSemantic)
    .sort((a, b) => depthOf(a._id) - depthOf(b._id));

  const groups = [];
  for (const node of candidates) {
    if (groups.some((g) => isDescendantOf(nodes, node._id, g.root._id)))
      continue;
    groups.push({
      root: node,
      members: [node, ...descendantsOf(nodes, node._id)],
    });
  }
  return groups;
}

// Every light with an AI description or reference image, restricted to ones
// the user has actually made visible in the scene (node.visibleInViewport —
// see PropertiesPanel.jsx's "Light Settings" tab and its marker sphere in
// sceneGraph.js's buildObject3D). An invisible light has nothing for the
// model to see in the beauty render, so pairing it with a bounding box there
// would only invite hallucination the same way an out-of-frame object would
// — see buildSceneJson's "lights" filtering below. No grouping with
// descendants (a light's children, if any, aren't part of "what the light
// looks like") — just the one visible sphere standing in for it (see
// computeLightBounds below).
export function buildSemanticLights(nodes) {
  return nodes.filter(
    (n) =>
      n.type === "light" &&
      n.visibleInViewport &&
      (!!n.aiDescription || (n.aiReferenceImages?.length ?? 0) > 0),
  );
}

// Most editor-only helpers (grid, TransformControls gizmo, selection
// outline, light/camera gizmos) are tagged `userData.isEditorHelper` at
// creation and hidden here via `.visible` for the duration of a render call
// — safe because none of them have real content nested underneath (unlike a
// "Null" node's AxesHelper, which is instead excluded via HELPER_LAYER; see
// that constant's comment in sceneGraph.js). Always called synchronously
// around a `renderer.render(...)` call, never across an `await`, so the live
// viewport's own render loop — which shares this same renderer/scene — can
// never observe the hidden state.
function withHelpersHidden(scene, fn) {
  const hidden = [];
  scene.traverse((o) => {
    if (o.userData.isEditorHelper && o.visible) {
      o.visible = false;
      hidden.push(o);
    }
  });
  try {
    return fn();
  } finally {
    for (const o of hidden) o.visible = true;
  }
}

// Deterministic, maximally-distinct hue per sequential index (the golden
// angle guarantees consecutive indices land far apart on the color wheel,
// unlike e.g. hashing each group's id independently) — fixed
// saturation/lightness so every box reads as an equally vivid, legible
// stroke color regardless of how many objects the scene has.
export function colorForObjectIndex(index) {
  const hue = (index * 137.508) % 360;
  return hslToHex(hue, 70, 55);
}

function hslToHex(h, s, l) {
  s /= 100;
  l /= 100;
  const k = (n) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n) =>
    l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const toHex = (x) =>
    Math.round(x * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
}

const clipPoint = new THREE.Vector3();

// A geometry's index buffer is built once, up front, by every factory this
// editor uses (primitives in sceneGraph.js, glTF imports) and never mutated
// afterward — so its triangle-edge list is safe to compute once and reuse
// for as long as the geometry itself lives. That matters here specifically:
// the live "AI Objects" overlay (see drawAiObjectsOverlay in Viewport.jsx)
// recomputes bounds every animation frame, re-querying the same handful of
// geometries 60 times a second. Keyed by the geometry object itself, so a
// scene-graph rebuild (every node edit — see buildSceneGraph in
// sceneGraph.js — hands back brand-new geometry instances) naturally ages
// the old entries out once nothing references that geometry anymore.
const edgesByGeometry = new WeakMap();

// Every unique undirected triangle edge in `geometry`, as index pairs into
// its position attribute.
function edgesForGeometry(geometry) {
  const cached = edgesByGeometry.get(geometry);
  if (cached) return cached;

  const index = geometry.index;
  const triangleCount = index
    ? index.count / 3
    : geometry.attributes.position.count / 3;
  const vertexAt = (i) => (index ? index.getX(i) : i);

  const seen = new Set();
  const edges = [];
  const addEdge = (a, b) => {
    const key = a < b ? `${a}_${b}` : `${b}_${a}`;
    if (seen.has(key)) return;
    seen.add(key);
    edges.push([a, b]);
  };
  for (let t = 0; t < triangleCount; t++) {
    const a = vertexAt(t * 3);
    const b = vertexAt(t * 3 + 1);
    const c = vertexAt(t * 3 + 2);
    addEdge(a, b);
    addEdge(b, c);
    addEdge(c, a);
  }

  edgesByGeometry.set(geometry, edges);
  return edges;
}

// A mesh's own real vertices in world space — NOT its local bounding box's 8
// corners (the previous approach). A box's corners sit well outside any
// curved shape's actual surface (a sphere's local AABB is the cube
// circumscribing it, so projecting *that* cube's corners drew a noticeably
// larger box than the sphere itself, even dead-center in frame with nothing
// clipped) — real vertices are the only thing guaranteed to trace the mesh's
// actual silhouette, for any geometry, curved or not. (This also
// subsumes what the box-corner approach got right for a flat, rotated plane
// — its own real vertices *are* its 4 corners, so nothing changes for that
// case.)
function worldVerticesForMesh(obj) {
  const position = obj.geometry?.attributes?.position;
  if (!position?.count) return null;
  const vertices = new Array(position.count);
  for (let i = 0; i < position.count; i++) {
    vertices[i] = new THREE.Vector3()
      .fromBufferAttribute(position, i)
      .applyMatrix4(obj.matrixWorld);
  }
  return vertices;
}

// The camera's six view-space frustum half-spaces (near, far, left, right,
// top, bottom), each returning <= 0 for "inside" that plane — derived
// straight from its own vertical fov/aspect/near/far, the same symmetric
// frustum a THREE.PerspectiveCamera always builds.
function frustumPlanes(camera) {
  const tanY = Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
  const tanX = tanY * camera.aspect;
  const { near, far } = camera;
  return [
    (p) => p.z + near, // near
    (p) => -p.z - far, // far
    (p) => p.x + p.z * tanX, // right
    (p) => -p.x + p.z * tanX, // left
    (p) => p.y + p.z * tanY, // top
    (p) => -p.y + p.z * tanY, // bottom
  ];
}

// Clips one edge (in view space) against every frustum plane at once —
// Liang-Barsky: each plane trims the edge's parametric [0, 1] range down to
// where that plane's own test is satisfied, and the surviving range is the
// intersection across all six. This — rather than a per-corner in/out test —
// is what correctly handles a corner sitting very close to the camera at a
// steep angle: on its own, such a corner can project to a wildly exaggerated
// screen position in every direction at once (a near-zero perspective
// divisor blows up both x and y together), even though the actual visible
// sliver of the edge connecting it to the rest of the object is only
// partially inside the frustum. A prior version of this only clipped against
// the near plane and left the other five to a post-projection clamp, which
// is exactly what produced a bounding box reaching well past a plane's
// actual visible edge, out towards a far corner that was never really in
// frame — clamping a single already-exaggerated point can't fix that; only
// clipping the edge itself, before that blow-up happens, can. Returns null
// if none of the edge survives all six planes.
function clipEdge(pa, pb, planes) {
  let tMin = 0;
  let tMax = 1;
  for (const f of planes) {
    const d0 = f(pa);
    const d1 = f(pb);
    const delta = d1 - d0;
    if (delta === 0) {
      if (d0 > 0) return null; // the whole edge is outside this plane
      continue;
    }
    const t = -d0 / delta; // where the edge crosses this plane
    if (delta > 0) tMax = Math.min(tMax, t);
    else tMin = Math.max(tMin, t);
    if (tMin > tMax) return null;
  }
  return [
    new THREE.Vector3().lerpVectors(pa, pb, tMin),
    new THREE.Vector3().lerpVectors(pa, pb, tMax),
  ];
}

// Every frustum-clipped view-space point of a mesh's own real triangle edges
// — clipping those edges (not just testing each vertex individually) so the
// result is the true tight silhouette, not an overestimate from a vertex
// that's outside the frustum but whose connecting edges still cross through
// visible space.
function clippedViewPoints(worldVertices, edges, camera, planes) {
  const viewVertices = worldVertices.map((p) =>
    p.clone().applyMatrix4(camera.matrixWorldInverse),
  );
  const points = [];
  for (const [a, b] of edges) {
    const clipped = clipEdge(viewVertices[a], viewVertices[b], planes);
    if (clipped) points.push(...clipped);
  }
  return points;
}

// Every frustum-visible view-space point across every mesh in a semantic
// group, unioned together — per-mesh (not one box for the whole group) so a
// compound object's own members each contribute their real, correctly
// oriented footprint rather than one another's world-axis-aligned bounds.
function groupViewPoints(group, objectsById, camera, planes) {
  const points = [];
  for (const member of group.members) {
    const obj = objectsById.get(member._id);
    const vertices = obj && worldVerticesForMesh(obj);
    if (!vertices) continue;
    const edges = edgesForGeometry(obj.geometry);
    points.push(...clippedViewPoints(vertices, edges, camera, planes));
  }
  return points;
}

// The 2D screen-space AABB of a set of already frustum-clipped view-space
// points — always axis-aligned and camera-facing by construction, since it's
// derived from projected points rather than any 3D orientation. Returns null
// if there were no points at all (nothing in the group was visible). The
// clamp to [0, 1] is just a float-precision safety net at this point — every
// point already satisfied all six frustum planes in clipEdge above, so it
// should already land in range.
function screenRectFromViewPoints(points, camera) {
  if (!points.length) return null;

  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const p of points) {
    clipPoint.copy(p).applyMatrix4(camera.projectionMatrix);
    // Clip space [-1, 1] → normalized screen space [0, 1], y flipped (clip
    // space is up-positive, screen space is down-positive).
    const x = (clipPoint.x + 1) / 2;
    const y = (1 - clipPoint.y) / 2;
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  }
  minX = THREE.MathUtils.clamp(minX, 0, 1);
  minY = THREE.MathUtils.clamp(minY, 0, 1);
  maxX = THREE.MathUtils.clamp(maxX, 0, 1);
  maxY = THREE.MathUtils.clamp(maxY, 0, 1);
  return {
    x: minX,
    y: minY,
    width: Math.max(0, maxX - minX),
    height: Math.max(0, maxY - minY),
  };
}

// Which of the camera's four SCREEN edges (top/bottom/left/right — near/far
// depth clipping is deliberately excluded, since scene.json's vocabulary
// here is screen-edge direction only) cut off part of a group's true
// silhouette. Tests every member mesh's raw, UNclipped vertices directly
// against the four side frustum planes — unlike groupViewPoints/
// screenRectFromViewPoints, which only ever see the post-clip survivors, so
// a boundingBox edge sitting flush against the frame border looks identical
// there whether the object stops exactly at that edge or continues on for
// miles. A single vertex failing one of these tests is direct proof real
// geometry exists just past that edge. Ordered vertical-then-horizontal
// (top/bottom before left/right) to match how a corner reads out loud —
// "top left", not "left top".
function computeClippedSides(group, objectsById, camera, planes) {
  const [, , right, left, top, bottom] = planes;
  let hasTop = false,
    hasBottom = false,
    hasLeft = false,
    hasRight = false;
  for (const member of group.members) {
    const obj = objectsById.get(member._id);
    const vertices = obj && worldVerticesForMesh(obj);
    if (!vertices) continue;
    for (const worldPoint of vertices) {
      const p = worldPoint.clone().applyMatrix4(camera.matrixWorldInverse);
      if (top(p) > 0) hasTop = true;
      if (bottom(p) > 0) hasBottom = true;
      if (left(p) > 0) hasLeft = true;
      if (right(p) > 0) hasRight = true;
    }
  }
  const sides = [];
  if (hasTop) sides.push("top");
  if (hasBottom) sides.push("bottom");
  if (hasLeft) sides.push("left");
  if (hasRight) sides.push("right");
  return sides;
}

// One entry per group in `groups`, aligned by index (`null` where the group
// has no live/visible geometry, or none of it is inside the camera's frustum
// at all — see groupViewPoints/screenRectFromViewPoints) — the single source
// of truth for the live "AI Objects" viewport overlay, the InspectPanel.jsx
// preview, and the boundingBox/color attached to each scene.json object
// below, so what the user sees in the editor always matches what the
// generation model receives. `camera.updateMatrixWorld()` first since this
// is also called on a standalone export camera (see buildExportCamera)
// that's never part of a scene graph render loop and so never gets it
// called for free.
export function computeObjectBounds({ groups, objectsById, camera }) {
  camera.updateMatrixWorld();
  const planes = frustumPlanes(camera);
  return groups.map((group, index) => {
    const points = groupViewPoints(group, objectsById, camera, planes);
    const boundingBox = screenRectFromViewPoints(points, camera);
    if (!boundingBox) return null;
    // Only meaningful once we already know *something* of the group is on
    // screen (boundingBox exists) — an entirely out-of-frame group is
    // dropped by buildSceneJson before this would ever matter.
    const partiallyVisibleSide = computeClippedSides(
      group,
      objectsById,
      camera,
      planes,
    );
    return {
      color: colorForObjectIndex(index),
      boundingBox,
      partiallyVisible: partiallyVisibleSide.length > 0,
      partiallyVisibleSide,
    };
  });
}

// Same as computeObjectBounds, but keyed by each group's root node id
// instead of aligned by array index — for a consumer (InspectPanel.jsx) that
// computes its own `groups` independently rather than receiving the exact
// same array this function used, where index alignment can't be trusted.
export function computeObjectBoundsById({ nodes, objectsById, camera }) {
  const groups = buildSemanticGroups(nodes);
  const bounds = computeObjectBounds({ groups, objectsById, camera });
  return Object.fromEntries(
    groups.map((g, i) => [g.root._id, bounds[i]]).filter(([, b]) => b),
  );
}

// One entry per light in `lights`, aligned by index (`null` where the
// light's visible marker sphere has no live geometry, or none of it is
// inside the camera's frustum) — the lights counterpart to
// computeObjectBounds above, built from the exact same real-geometry/
// real-edge frustum clipping (worldVerticesForMesh/edgesForGeometry) applied
// to the light's own visible sphere (see buildObject3D's light branch in
// sceneGraph.js, which stashes it at group.userData.visibleSphere) rather
// than a mesh from a semantic group — a light has no group/members of its
// own, just the one sphere standing in for it.
export function computeLightBounds({ lights, objectsById, camera }) {
  camera.updateMatrixWorld();
  const planes = frustumPlanes(camera);
  return lights.map((node, index) => {
    const sphere = objectsById.get(node._id)?.userData.visibleSphere;
    const vertices = sphere && worldVerticesForMesh(sphere);
    if (!vertices) return null;
    const edges = edgesForGeometry(sphere.geometry);
    const points = clippedViewPoints(vertices, edges, camera, planes);
    const boundingBox = screenRectFromViewPoints(points, camera);
    if (!boundingBox) return null;
    return { color: colorForObjectIndex(index), boundingBox };
  });
}

// Same as computeLightBounds, but keyed by each light's node id — the
// lights counterpart to computeObjectBoundsById, for a consumer
// (InspectPanel.jsx) that computes its own `lights` array independently.
export function computeLightBoundsById({ nodes, objectsById, camera }) {
  const lights = buildSemanticLights(nodes);
  const bounds = computeLightBounds({ lights, objectsById, camera });
  return Object.fromEntries(
    lights.map((n, i) => [n._id, bounds[i]]).filter(([, b]) => b),
  );
}

// ambientLightColor/node.lightColor are only ever hex at their *default*
// value ("#ffffff") — ColorPicker.jsx's onChange always writes back a plain
// `rgba(r, g, b, a)` CSS string the moment a user actually picks a color
// (see its rgbaToString), never hex, so either shape can show up here.
// buildEnvironmentInfo's whole point is giving the generation model a clean,
// unambiguous color per light — reusing ColorPicker's own parser (rather
// than a second, possibly-diverging regex) guarantees this agrees with what
// the color swatch in the editor actually shows. Alpha is dropped: nothing
// downstream of scene.json has a use for a light's own transparency.
function toHexColor(color) {
  return rgbaToHex(parseColorToRgba(color));
}

// Shared bucket boundaries for both ambient and per-light intensity, used by
// buildEnvironmentInfo below — a prose label reads far more reliably to a
// generation model than a bare unitless float (same reasoning as buildPrompt
// favoring prose over a raw JSON dump — see aiRender.js).
function intensityLabel(value) {
  const v = value ?? 0;
  if (v <= 0.25) return "low";
  if (v <= 0.5) return "medium low";
  if (v <= 0.75) return "medium high";
  if (v <= 1) return "high";
  return "very high";
}

// How far off camera-local +Y/+X/-Z a normalized direction has to sit before
// it reads as a distinct "top"/"right"/"front" etc. rather than roughly
// centered on that axis (~20.5°, `asin(0.35)`) — see
// classifyRelativePosition below for how the three axes combine.
const RELATIVE_POSITION_THRESHOLD = 0.35;

// Coarse position of `worldPos` relative to `camera` (already
// updateMatrixWorld()'d), described the way a cinematographer would name a
// light's role in the shot — top/center/bottom optionally combined with
// left/center/right (e.g. "Top Left", or just "Top" when left-right is
// roughly centered), falling back to a single front/back word only when
// NEITHER vertical nor horizontal is off-center. Built from the point's
// direction in camera-local space (THREE convention: local +X right, +Y up,
// -Z forward).
//
// front/back is named for lighting role, NOT raw spatial position relative
// to the lens — the two are inverted. A light out in the scene, roughly
// along the camera's own forward axis (local z < 0 — spatially "in front of"
// the camera), sits on/beyond the far side of whatever it's lighting and
// faces back toward the lens: that's backlighting/rim lighting, so it's
// labeled "back". A light near/behind the camera (local z > 0 — spatially
// "behind" the lens, roughly where the camera operator stands) shines the
// same direction the camera looks, illuminating the subject's near side from
// the camera's own vantage point: that's front lighting, labeled "front".
//
// front/back is deliberately the *lowest*-priority word, not a third fact
// tacked on whenever a slot is free: almost every light in a scene sits
// somewhere along the camera's forward axis (that's where the visible
// content is), so filling a spare slot with "back" any time only one of
// vertical/horizontal applied would make it show up on nearly every light,
// burying the genuinely rare/notable case — a light with no clear vertical
// or horizontal offset at all, meaning it's close to dead-ahead or literally
// behind the camera. That's also the one case simple unit-vector geometry
// guarantees a decisive z: if |x| and |y| both fall under the threshold,
// x²+y²+z²=1 forces |z| comfortably over it. The final `["center"]`
// fallback is a safety net for true edge cases (float precision at the
// exact threshold, or this threshold being tuned later) rather than a
// normally-reachable path.
function classifyRelativePosition(worldPos, camera) {
  const local = worldPos.clone().applyMatrix4(camera.matrixWorldInverse);
  if (local.lengthSq() < 1e-6) return ["center"]; // effectively at the camera itself
  local.normalize();
  const words = [];
  if (local.y > RELATIVE_POSITION_THRESHOLD) words.push("top");
  else if (local.y < -RELATIVE_POSITION_THRESHOLD) words.push("bottom");
  if (local.x > RELATIVE_POSITION_THRESHOLD) words.push("right");
  else if (local.x < -RELATIVE_POSITION_THRESHOLD) words.push("left");
  if (words.length === 0) {
    // Inverted vs. raw spatial front/behind — see the lighting-role comment
    // above.
    if (local.z < -RELATIVE_POSITION_THRESHOLD) words.push("back");
    else if (local.z > RELATIVE_POSITION_THRESHOLD) words.push("front");
  }
  return words.length ? words.slice(0, 2) : ["center"];
}

// The scene's overall physical lighting setup — ambient fill plus every real
// light node's color/intensity/position relative to the camera — for
// buildSceneJson's "environment" key. Deliberately independent of
// buildSemanticLights' filtered "lights" array below (visibleInViewport AND
// has an aiDescription/reference image, paired with a screen-space bounding
// box): that array exists to ground specific lights the user singled out for
// a description, while this one exists to give the generation model an
// accurate sense of the render's overall lighting mood, which shouldn't
// depend on whether any individual light happens to have its own marker
// sphere toggled on or a description written for it.
export function buildEnvironmentInfo({
  nodes,
  objectsById,
  camera,
  ambientLightColor,
  ambientLightIntensity,
}) {
  camera.updateMatrixWorld();
  const lightWorldPos = new THREE.Vector3();
  const lighting = nodes
    .filter((n) => n.type === "light")
    .map((node) => {
      // The real THREE light's own world position (parent transforms already
      // folded in), not a re-derivation from node.transform — mirrors
      // computeLightBounds' use of objectsById above. Skipped, not guessed,
      // if the scene graph hasn't attached this node yet.
      const light = objectsById.get(node._id)?.userData.light;
      if (!light) return null;
      light.getWorldPosition(lightWorldPos);
      return {
        color: toHexColor(node.lightColor || "#ffffff"),
        intensity: intensityLabel(node.intensity ?? 1),
        position: classifyRelativePosition(lightWorldPos, camera),
      };
    })
    .filter(Boolean);
  return {
    ambientColor: toHexColor(ambientLightColor || "#ffffff"),
    ambientIntensity: intensityLabel(ambientLightIntensity ?? 0),
    lighting,
  };
}

// A standalone camera decoupled from the live scene graph and from whichever
// camera object the viewport happens to be using (the free camera, or a
// piloted scene camera's real PerspectiveCamera nested at identity-local
// inside its node's Group) — reading world position/quaternion rather than
// local is what makes this correct for the piloted case. Never added to the
// scene graph and never mutates `sourceCamera`, so the live viewport's own
// aspect/framing is untouched regardless of the requested export resolution.
export function buildExportCamera(sourceCamera, width, height) {
  const pos = new THREE.Vector3();
  const quat = new THREE.Quaternion();
  sourceCamera.getWorldPosition(pos);
  sourceCamera.getWorldQuaternion(quat);
  const camera = new THREE.PerspectiveCamera(
    sourceCamera.fov,
    width / height,
    sourceCamera.near,
    sourceCamera.far,
  );
  camera.position.copy(pos);
  camera.quaternion.copy(quat);
  camera.updateProjectionMatrix();
  return camera;
}

function flipRowsVertically(bytes, width, height) {
  const rowBytes = width * 4;
  const tmp = new Uint8Array(rowBytes);
  for (let y = 0; y < height / 2; y++) {
    const top = y * rowBytes;
    const bottom = (height - 1 - y) * rowBytes;
    tmp.set(bytes.subarray(top, top + rowBytes));
    bytes.set(bytes.subarray(bottom, bottom + rowBytes), top);
    bytes.set(tmp, bottom);
  }
}

// WebGL readback is bottom-to-top; canvas ImageData is top-to-bottom.
function bytesToBlob(bytes, width, height) {
  flipRowsVertically(bytes, width, height);
  const canvas = document.createElement("canvas"); // detached, never appended to the DOM
  canvas.width = width;
  canvas.height = height;
  canvas
    .getContext("2d")
    .putImageData(
      new ImageData(
        new Uint8ClampedArray(bytes.buffer, bytes.byteOffset, bytes.length),
        width,
        height,
      ),
      0,
      0,
    );
  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

// three.js only applies `renderer.outputColorSpace` (sRGB) when rendering
// straight to the canvas — WebGLRenderer internally forces linear output for
// any other render target (see `_currentRenderTarget === null` check in
// WebGLRenderer.js), regardless of this renderer's own outputColorSpace.
// This pass renders to an offscreen WebGLRenderTarget, so the raw readback is
// always linear; reading it straight into a PNG (which every viewer
// interprets as sRGB) reads visibly darker than the live canvas.
const SRGB_ENCODE_LUT = (() => {
  const lut = new Uint8Array(256);
  for (let i = 0; i < 256; i++) {
    const c = i / 255;
    const encoded =
      c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
    lut[i] = Math.round(Math.min(1, Math.max(0, encoded)) * 255);
  }
  return lut;
})();

function srgbEncodeInPlace(bytes) {
  for (let i = 0; i < bytes.length; i += 4) {
    bytes[i] = SRGB_ENCODE_LUT[bytes[i]];
    bytes[i + 1] = SRGB_ENCODE_LUT[bytes[i + 1]];
    bytes[i + 2] = SRGB_ENCODE_LUT[bytes[i + 2]];
  }
}

// Clean RGB render from the export camera — a normal render with editor
// helpers/gizmos hidden, nothing else altered — sRGB-encoded to match what
// the live canvas actually displays (see srgbEncodeInPlace above).
export async function renderBeauty(
  renderer,
  scene,
  exportCamera,
  width,
  height,
) {
  // Anti-aliased via native MSAA (the `samples` option), NOT because the live
  // viewport uses MSAA — it deliberately doesn't (see Viewport.jsx's renderer
  // setup: `antialias:true` never engages once rendering goes through
  // EffectComposer, so SMAAPass is what actually smooths the *live* canvas
  // instead) — but this export path bypasses that whole composer chain
  // entirely (a single direct `renderer.render()` call below, no SMAAPass,
  // no OutputPass), so without requesting MSAA here explicitly this render
  // got zero anti-aliasing of any kind: raw jagged edges at every export
  // resolution, which read as "low quality" independent of how high the
  // requested width/height actually was. Clamped to what this GPU actually
  // supports — some report 0 (rare, but `new WebGLRenderTarget` would
  // otherwise silently no-op the request rather than error).
  const samples = Math.min(4, renderer.capabilities.maxSamples ?? 0);
  const target = new THREE.WebGLRenderTarget(width, height, { samples });
  // This render call never reaches an OutputPass (no composer involved) — so
  // any mesh with an active projection (projection.js) needs its plain
  // linear value here, not the pre-correction meant to cancel out that
  // pass's later tonemapping. See projectionOutputPassCompensation's own
  // comment for why the same shader has to behave differently for the two
  // cases.
  projectionOutputPassCompensation.value = false;
  try {
    withHelpersHidden(scene, () => {
      renderer.setRenderTarget(target);
      renderer.render(scene, exportCamera);
      renderer.setRenderTarget(null);
    });
  } finally {
    projectionOutputPassCompensation.value = true;
  }
  try {
    const buf = new Uint8Array(width * height * 4);
    renderer.readRenderTargetPixels(target, 0, 0, width, height, buf);
    srgbEncodeInPlace(buf);
    return await bytesToBlob(buf, width, height);
  } finally {
    target.dispose();
  }
}

// Reference images are picker values from MiniAssetPicker/ElementsRow (see
// PropertiesPanel.jsx's "AI" tab) — a plain URL string, a rich
// `{url, thumbnail_url}` object, or, before a save resolves it, a local
// pending-upload wrapper with no real, externally-reachable URL yet. Only
// real URLs are useful to a generation model running server-side.
function referenceImageUrl(img) {
  if (!img) return null;
  if (typeof img === "string") return img;
  if (img.__pending) return null;
  return img.url || null;
}

// Pure function — deliberately minimal: just what the generation prompt
// actually needs. `objectBounds` (see computeObjectBounds/
// computeObjectBoundsById above) is optional and aligned by index with
// `groups` — a caller with no bounds to offer can just omit it entirely,
// which keeps every group and drops `color`/`boundingBox` from its JSON
// (`undefined` values are dropped by JSON.stringify), since not having
// computed visibility isn't the same as knowing an object is out of frame.
// A caller that *does* supply objectBounds is asserting it knows which
// groups are actually visible — a `null` entry there means that group's
// whole geometry is outside the camera's frustum, so it's dropped from
// "objects" altogether rather than sent with no color/box: an object the
// model can't see in the beauty render has nothing for it to correlate a
// description to, and a stray screen region to plausibly match anyway would
// only invite it to hallucinate one.
// Presented to the model alongside the (separately rendered, see
// runAIRenderPipeline) texture-reference image — spelled out explicitly
// because a projection's own known limitations (facing+frustum-only
// coverage, no soft falloff, occlusion gaps) mean it can have blank/
// stretched/wrong-angle areas, and a model taking it as a literal, complete
// render would either hallucinate detail into its gaps or let those gaps
// visibly bleed into the final image.
const TEXTURE_REFERENCE_NOTE =
  "A separately rendered pass showing this scene's saved texture projection, baked onto whatever surfaces it covers, from this exact same camera angle. Treat it strictly as a styling/coloring guide for the surfaces it does show clearly — never as a literal or complete render. It can be incomplete: gaps with no texture data at all, stretching, or wrong-looking areas on surfaces the projection doesn't cleanly reach (occluded by another object, facing away, or outside its original frame). Do not read a gap or artifact in this image as \"this surface should look empty/blank\" — for anything this image doesn't clearly and confidently show, or where it disagrees with @image1, defer entirely to the Beauty Render's own composition, silhouettes, materials, and occlusion.";

export function buildSceneJson({
  sceneSettings,
  sourceCamera,
  groups,
  objectBounds,
  lights = [],
  lightBounds,
  hasTextureReference = false,
  environment,
}) {
  return {
    world: {
      description: sceneSettings?.aiDescription || undefined,
      referenceImages: (sceneSettings?.aiReferenceImages || [])
        .map(referenceImageUrl)
        .filter(Boolean),
    },
    camera: { fov: sourceCamera.fov },
    // The scene's overall lighting setup (buildEnvironmentInfo above) —
    // ambient fill plus every real light's color/intensity/relative
    // position, independent of the per-light "lights" array below.
    environment: environment || undefined,
    // `image` (the "@imageN" label) is filled in later by aiRender.js's
    // buildImageManifest, once the texture-reference render has actually
    // been uploaded — this only marks that one exists and why.
    textureReference: hasTextureReference
      ? { note: TEXTURE_REFERENCE_NOTE }
      : undefined,
    objects: groups
      .map((g, i) => {
        const bounds = objectBounds?.[i];
        if (objectBounds && !bounds) return null; // out of frame — drop it
        return {
          description: g.root.aiDescription || undefined,
          referenceImages: (g.root.aiReferenceImages || [])
            .map(referenceImageUrl)
            .filter(Boolean),
          color: bounds?.color,
          boundingBox: bounds?.boundingBox,
          // Whether part of this object's true silhouette falls outside the
          // frame (and, if so, which screen edge(s) it's cut off by — see
          // computeObjectBounds/computeClippedSides above), so the model
          // knows a bounding box flush against the frame border means the
          // object continues past it, not that it happens to end exactly
          // there.
          partiallyVisible: bounds?.partiallyVisible ?? false,
          partiallyVisibleSide: bounds?.partiallyVisibleSide ?? [],
        };
      })
      .filter(Boolean),
    // Same visibility contract as "objects" above: a light only makes it in
    // once we actually know it's in frame (lightBounds supplied and this
    // light's own entry isn't null).
    lights: lights
      .map((node, i) => {
        const bounds = lightBounds?.[i];
        if (lightBounds && !bounds) return null; // out of frame — drop it
        return {
          description: node.aiDescription || undefined,
          referenceImages: (node.aiReferenceImages || [])
            .map(referenceImageUrl)
            .filter(Boolean),
          color: bounds?.color,
          boundingBox: bounds?.boundingBox,
        };
      })
      .filter(Boolean),
  };
}

// Orchestrator: builds the export camera, renders the single beauty pass,
// and assembles the scene.json describing it. `objectsById` (a node id →
// live Object3D map — see Viewport.jsx's objectsByIdRef) is only needed for
// computeObjectBounds; bounds are computed through the *export* camera, not
// whatever the live viewport happens to be framing, so they match the
// beauty image's own resolution/aspect exactly.
export async function runAIRenderPipeline({
  renderer,
  scene,
  sourceCamera,
  nodes,
  sceneSettings,
  objectsById,
  width,
  height,
  hasActiveProjection = false,
  ambientLightColor,
  ambientLightIntensity,
}) {
  const exportCamera = buildExportCamera(sourceCamera, width, height);

  // Always the scene's own real materials/lighting — never whatever a
  // currently-active projection is showing in the live editor (see
  // projection.js's projectionEnabled) — restored right after regardless of
  // how the render itself goes.
  const previousProjectionEnabled = projectionEnabled.value;
  projectionEnabled.value = false;
  let beautyBlob;
  try {
    beautyBlob = await renderBeauty(
      renderer,
      scene,
      exportCamera,
      width,
      height,
    );
  } finally {
    projectionEnabled.value = previousProjectionEnabled;
  }

  // A second, separate pass — only while a projection is actually active —
  // capturing what it shows from this same angle, sent as its own "texture
  // reference" image (see buildSceneJson's TEXTURE_REFERENCE_NOTE for how
  // it's framed to the model).
  const textureReferenceBlob = hasActiveProjection
    ? await renderBeauty(renderer, scene, exportCamera, width, height)
    : null;

  const groups = buildSemanticGroups(nodes);
  const objectBounds = computeObjectBounds({
    groups,
    objectsById,
    camera: exportCamera,
  });
  const lights = buildSemanticLights(nodes);
  const lightBounds = computeLightBounds({
    lights,
    objectsById,
    camera: exportCamera,
  });
  const environment = buildEnvironmentInfo({
    nodes,
    objectsById,
    camera: exportCamera,
    ambientLightColor,
    ambientLightIntensity,
  });
  const sceneJson = buildSceneJson({
    sceneSettings,
    sourceCamera,
    groups,
    objectBounds,
    lights,
    lightBounds,
    hasTextureReference: !!textureReferenceBlob,
    environment,
  });
  return { beautyBlob, textureReferenceBlob, sceneJson };
}
